import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Link, Navigate, useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { ShieldCheck, Upload, ArrowLeft, FileText, CheckCircle2, Clock, XCircle, AlertTriangle } from 'lucide-react';
import { useSatisfinderAuth } from '@/contexts/SatisfinderAuthContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ID_TYPES = ['Passport', 'National ID', 'Driver\u2019s license', 'Other government-issued ID'];
const ID_TYPE_MAP = { 'Driver\u2019s license': 'Driver\'s license' };
const MAX_MB = 10;
const ALLOWED = ['application/pdf', 'image/png', 'image/jpeg', 'image/webp'];

/**
 * /satisfinder/kyc — Manual KYC submission for the authenticated satisFINDER.
 * Document uploads go to a private S3 prefix; only reviewer-approved workflows
 * can generate a short-lived presigned URL to view the file.
 */
export default function SatisfinderKYC() {
  const { me, loading, refresh } = useSatisfinderAuth();
  const nav = useNavigate();
  const fileRef = useRef(null);
  const [state, setState] = useState(null);
  const [form, setForm] = useState({
    full_legal_name: '',
    date_of_birth: '',
    country_of_id: '',
    id_type: 'Passport',
    document_expiry_date: '',
  });
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const r = await fetch(`${API}/satisfinder/kyc/me`, { credentials: 'include' });
        if (r.ok) setState(await r.json());
      } catch { /* ignored */ }
    })();
  }, []);

  useEffect(() => {
    if (me?.full_name && !form.full_legal_name) {
      setForm(p => ({ ...p, full_legal_name: me.full_name, country_of_id: me.country || '' }));
    }
  }, [me]);  // eslint-disable-line react-hooks/exhaustive-deps

  if (loading) return <Loading />;
  if (!me) return <Navigate to="/satisfinder/login" replace />;

  const status = state?.status || me.kyc_status || 'not_started';
  const canEdit = status === 'not_started' || status === 'rejected' || status === 'needs_update';

  const submit = async (e) => {
    e.preventDefault();
    if (!file) { toast.error('Please upload your ID document.'); return; }
    if (!ALLOWED.includes(file.type)) { toast.error('Upload a PDF, PNG, or JPEG.'); return; }
    if (file.size > MAX_MB * 1024 * 1024) { toast.error(`File must be under ${MAX_MB}MB.`); return; }

    setBusy(true);
    try {
      const fd = new FormData();
      fd.append('full_legal_name', form.full_legal_name);
      fd.append('date_of_birth', form.date_of_birth);
      fd.append('country_of_id', form.country_of_id);
      fd.append('id_type', ID_TYPE_MAP[form.id_type] || form.id_type);
      if (form.document_expiry_date) fd.append('document_expiry_date', form.document_expiry_date);
      fd.append('document', file);
      const res = await fetch(`${API}/satisfinder/kyc`, { method: 'POST', credentials: 'include', body: fd });
      const d = await res.json().catch(() => ({}));
      if (!res.ok) { toast.error(d.detail || 'Submission failed'); return; }
      toast.success('KYC submitted for review.');
      await refresh();
      const r = await fetch(`${API}/satisfinder/kyc/me`, { credentials: 'include' });
      if (r.ok) setState(await r.json());
    } finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen bg-[#F8FAFC]">
      <Header />
      <main className="max-w-3xl mx-auto px-6 py-10 space-y-6" data-testid="sf-kyc-page">
        <button onClick={() => nav('/satisfinder/dashboard')} className="text-sm text-[#475569] hover:text-[#0F172A] inline-flex items-center gap-1">
          <ArrowLeft className="w-4 h-4" /> Back to dashboard
        </button>

        <div className="bg-white rounded-2xl border border-[#E2E8F0] p-8">
          <div className="flex items-center gap-2 text-[#1A3E6F]">
            <ShieldCheck className="w-5 h-5" />
            <span className="text-[10px] uppercase tracking-widest font-bold">KYC Verification</span>
          </div>
          <h1 className="font-['Montserrat'] text-2xl lg:text-3xl font-bold mt-2">KYC Verification</h1>

          <p className="text-sm text-[#475569] mt-4 leading-relaxed">
            KYC may be required before selected paid FINDs, sensitive projects, high-value assignments, or payout.
          </p>
          <div className="mt-3 bg-[#EFF6FF] border border-[#BFDBFE] rounded-lg p-4 text-xs text-[#1E3A8A] leading-relaxed">
            <p className="font-semibold mb-1">Your privacy is protected:</p>
            Your ID is used only to verify eligibility and protect the integrity of satisFIND® projects. Your documents are stored securely and are only accessible to authorised satisFIND® reviewers. Your ID will not be shown publicly or shared with other satisFINDERs. We do not use your ID for biometric identification.
          </div>

          <StatusBanner status={status} notes={state?.reviewer_notes} />

          {canEdit ? (
            <form onSubmit={submit} className="mt-6 space-y-4">
              <Field label="Full legal name" required>
                <input required value={form.full_legal_name} onChange={(e) => setForm(p => ({ ...p, full_legal_name: e.target.value }))} className={inputCls} data-testid="kyc-legal-name" />
              </Field>
              <div className="grid grid-cols-2 gap-3">
                <Field label="Date of birth" required>
                  <input required type="date" value={form.date_of_birth} onChange={(e) => setForm(p => ({ ...p, date_of_birth: e.target.value }))} className={inputCls} data-testid="kyc-dob" />
                </Field>
                <Field label="Country of ID" required>
                  <input required value={form.country_of_id} onChange={(e) => setForm(p => ({ ...p, country_of_id: e.target.value }))} className={inputCls} data-testid="kyc-country" />
                </Field>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <Field label="ID type" required>
                  <select value={form.id_type} onChange={(e) => setForm(p => ({ ...p, id_type: e.target.value }))} className={inputCls} data-testid="kyc-id-type">
                    {ID_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
                  </select>
                </Field>
                <Field label="Document expiry date">
                  <input type="date" value={form.document_expiry_date} onChange={(e) => setForm(p => ({ ...p, document_expiry_date: e.target.value }))} className={inputCls} data-testid="kyc-expiry" />
                </Field>
              </div>
              <Field label="Upload ID document" required helper="PDF, PNG, or JPEG · Max 10MB · Private & encrypted at rest">
                <div className="flex items-center gap-3">
                  <button type="button" onClick={() => fileRef.current?.click()} className="inline-flex items-center gap-2 border border-[#CBD5E1] hover:border-[#1A3E6F] px-4 py-2 rounded-lg text-sm" data-testid="kyc-upload-btn">
                    <Upload className="w-4 h-4" /> Choose file
                  </button>
                  <span className="text-sm text-[#475569] truncate">
                    {file ? <><FileText className="w-4 h-4 inline mr-1" />{file.name}</> : 'No file chosen'}
                  </span>
                  <input ref={fileRef} type="file" hidden accept=".pdf,.png,.jpg,.jpeg,.webp,application/pdf,image/*" onChange={(e) => setFile(e.target.files?.[0] || null)} data-testid="kyc-file-input" />
                </div>
              </Field>
              <button type="submit" disabled={busy} className="w-full bg-[#1A3E6F] hover:bg-[#12305A] disabled:bg-[#94A3B8] text-white font-semibold py-3 rounded-lg text-sm mt-2" data-testid="kyc-submit">
                {busy ? 'Submitting…' : 'Submit for review'}
              </button>
            </form>
          ) : (
            <div className="mt-6 bg-[#F1F5F9] rounded-lg p-4 text-sm text-[#475569]">
              Your KYC is currently <strong>{statusLabel(status)}</strong>. If you need to make a change, contact satisFIND® support.
            </div>
          )}
        </div>
      </main>
    </div>
  );
}

function StatusBanner({ status, notes }) {
  if (status === 'not_started') return null;
  const cfg = {
    pending_review: { tone: 'amber', icon: Clock,        label: 'Pending review',   text: 'Our team is reviewing your KYC submission. This typically takes 1–3 business days.' },
    verified:       { tone: 'green', icon: CheckCircle2, label: 'Verified',         text: 'Your identity has been verified. You\u2019re eligible for paid FINDs.' },
    rejected:       { tone: 'red',   icon: XCircle,      label: 'Rejected',         text: 'Your submission was rejected. Please resubmit with the reviewer notes below.' },
    needs_update:   { tone: 'amber', icon: AlertTriangle,label: 'Needs update',     text: 'Additional information required. Please resubmit with the reviewer notes below.' },
  }[status];
  if (!cfg) return null;
  const tone = {
    green: 'bg-emerald-50 border-emerald-200 text-emerald-800',
    amber: 'bg-amber-50 border-amber-200 text-amber-800',
    red: 'bg-rose-50 border-rose-200 text-rose-700',
  }[cfg.tone];
  const Icon = cfg.icon;
  return (
    <div className={`mt-5 border rounded-lg p-4 ${tone}`} data-testid={`kyc-status-${status}`}>
      <div className="flex items-start gap-2">
        <Icon className="w-5 h-5 shrink-0 mt-0.5" />
        <div className="min-w-0">
          <p className="font-semibold">{cfg.label}</p>
          <p className="text-xs mt-1">{cfg.text}</p>
          {notes && (
            <div className="mt-2 text-xs bg-white/60 border border-current/20 rounded px-3 py-2">
              <span className="font-semibold">Reviewer note:</span> {notes}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function Header() {
  return (
    <header className="bg-white border-b border-[#E2E8F0]">
      <div className="max-w-3xl mx-auto px-6 h-14 flex items-center justify-between">
        <Link to="/satisfinder" className="flex items-center gap-2">
          <img src="/satisfind-logo.png" alt="satisFIND" style={{ height: 36, width: 'auto' }} />
        </Link>
      </div>
    </header>
  );
}

function Loading() {
  return <div className="min-h-screen flex items-center justify-center bg-[#F8FAFC] text-sm text-[#64748B]">Loading…</div>;
}

const inputCls = 'w-full px-3 py-2.5 border border-[#CBD5E1] rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#1A3E6F] focus:border-[#1A3E6F] bg-white';

function Field({ label, required, helper, children }) {
  return (
    <div>
      <label className="block text-xs font-semibold text-[#334155] mb-1">
        {label} {required && <span className="text-rose-500">*</span>}
      </label>
      {children}
      {helper && <p className="text-[10.5px] text-[#64748B] mt-1 leading-snug">{helper}</p>}
    </div>
  );
}

function statusLabel(s) {
  return { pending_review: 'pending review', verified: 'verified', rejected: 'rejected', needs_update: 'awaiting update' }[s] || s;
}
